import React, { useState, useCallback } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

export interface ImageSlide {
  src: string;
  alt?: string;
  title?: string;
}

export interface ImageLightboxProps {
  /** Liste des images (URL ou data URL). */
  images: ImageSlide[];
  /** Rendu des vignettes : reçoit une fonction openAt(index) pour ouvrir la lightbox à l'image index. */
  children: (openAt: (index: number) => void) => React.ReactNode;
  /** Activer le zoom (double-clic, molette, pinch). */
  zoom?: boolean;
}

/**
 * Lightbox réutilisable avec zoom. Passez les images et un rendu children(openAt).
 * Au clic sur une vignette, appelez openAt(index) pour ouvrir la lightbox sur cette image.
 */
const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, children, zoom = true }) => {
  const [index, setIndex] = useState<number | null>(null);

  const openAt = useCallback(
    (i: number) => {
      if (i >= 0 && i < images.length) setIndex(i);
    },
    [images.length]
  );

  const close = useCallback(() => setIndex(null), []);

  const slides = images.map((img) => ({
    src: img.src,
    alt: img.alt,
    title: img.title,
  }));

  return (
    <>
      {children(openAt)}
      {slides.length > 0 && index !== null && (
        <Lightbox
          open={true}
          close={close}
          index={index}
          slides={slides}
          plugins={zoom ? [Zoom] : undefined}
        />
      )}
    </>
  );
};

export default ImageLightbox;
